import type { TFunction } from 'i18next';
import {
  type Account,
  FieldMode,
  I18nCountry,
  JapanPrefecture,
  type UserProfileForm
} from '@linktivity/ars-neo';
import type { Rule } from '@linktivity/link-ui';
import {
  DOMAIN_LIKE_REGEX,
  NAME_REGEX,
  PASSPORT_NUMBER_REGEX,
  ROMAJI_REGEX
} from './regex';

/**
 * A profile field defaults to required when the site config leaves its mode
 * unspecified — or omits the config entirely — matching the behavior before
 * userProfileForm existed; only an explicit Optional or Hidden relaxes it.
 * Defaulting the other way silently dropped the nationality checks below on
 * every site whose config does not spell out FieldModeRequired.
 */
export const isProfileFieldRequired = (mode?: FieldMode): boolean =>
  mode !== FieldMode.FieldModeOptional && mode !== FieldMode.FieldModeHidden;

export const isProfileFieldVisible = (mode?: FieldMode): boolean =>
  mode !== FieldMode.FieldModeHidden;

export interface ProfileCompletenessOptions {
  requireJapanPostCode?: boolean;
}

/**
 * Returns true when the account has every field the site config marks as
 * required. Keep the required-ness semantics in sync with the ProfileForm
 * validation rules in packages/share/src/views/Profile/ProfileForm.tsx.
 * Presence-only by design: the form additionally enforces format rules
 * (name/email regexes), so the form is strictly stricter than this gate
 * and a redirect loop between them is impossible.
 */
export const isProfileComplete = (
  account: Pick<
    Account,
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'gender'
    | 'birthday'
    | 'country'
    | 'japanPrefecture'
    | 'postCode'
  >,
  userProfileForm?: UserProfileForm,
  options: ProfileCompletenessOptions = {}
): boolean => {
  if (
    isProfileFieldRequired(userProfileForm?.name) &&
    (!account.firstName?.trim() || !account.lastName?.trim())
  ) {
    return false;
  }

  if (
    isProfileFieldRequired(userProfileForm?.email) &&
    !account.email?.trim()
  ) {
    return false;
  }

  if (isProfileFieldRequired(userProfileForm?.gender) && !account.gender) {
    return false;
  }

  if (isProfileFieldRequired(userProfileForm?.birthday) && !account.birthday) {
    return false;
  }

  if (isProfileFieldRequired(userProfileForm?.nationality)) {
    if (!account.country || account.country === I18nCountry.Universal) {
      return false;
    }

    if (account.country === I18nCountry.Jpn) {
      const hasJapanPrefecture =
        !!account.japanPrefecture &&
        account.japanPrefecture !== JapanPrefecture.Oversea;
      const hasJapanPostCode =
        !options.requireJapanPostCode || !!account.postCode?.trim();

      return hasJapanPrefecture && hasJapanPostCode;
    }
  }

  return true;
};

export const createNameRules = (
  t: TFunction,
  fieldLabel: string,
  required = true
): Rule[] => [
  {
    required,
    message: t('share.validation.required', { field: fieldLabel }),
    trigger: ['change', 'blur']
  },
  {
    validator: (_, value) => {
      if (typeof value !== 'string') {
        return true;
      }

      const trimmedValue = value.trim();

      if (trimmedValue.length === 0) {
        return !required;
      }

      return (
        NAME_REGEX.test(trimmedValue) && !DOMAIN_LIKE_REGEX.test(trimmedValue)
      );
    },
    message: t('share.validation.nameInvalid', { field: fieldLabel }),
    trigger: ['change', 'blur']
  }
];

/**
 * Passport given/family names must be written in Roman letters (romaji), just
 * as they appear on the passport. Empty values are left to the required rule.
 */
export const createRomajiRules = (
  t: TFunction,
  fieldLabel: string,
  required = true
): Rule[] => [
  {
    required,
    message: t('share.validation.required', { field: fieldLabel }),
    trigger: ['change', 'blur']
  },
  {
    validator: (_, value) => {
      if (typeof value !== 'string') {
        return true;
      }

      const trimmedValue = value.trim();

      if (trimmedValue.length === 0) {
        return !required;
      }

      return (
        ROMAJI_REGEX.test(trimmedValue) && !DOMAIN_LIKE_REGEX.test(trimmedValue)
      );
    },
    message: t('share.validation.romajiOnly', { field: fieldLabel }),
    trigger: ['change', 'blur']
  }
];

/**
 * Passport numbers must match the half-width alphanumeric format. Empty values
 * are left to the required rule.
 */
export const createPassportNumberRules = (
  t: TFunction,
  fieldLabel: string,
  required = true
): Rule[] => [
  {
    required,
    message: t('share.validation.required', { field: fieldLabel }),
    trigger: ['change', 'blur']
  },
  {
    validator: (_, value) => {
      if (typeof value !== 'string') {
        return true;
      }

      const trimmedValue = value.trim();

      if (trimmedValue.length === 0) {
        return !required;
      }

      return PASSPORT_NUMBER_REGEX.test(trimmedValue);
    },
    message: t('share.validation.invalid', { field: fieldLabel }),
    trigger: ['change', 'blur']
  }
];
